import '@fontsource/open-sans'
import '@fontsource/roboto'
import './assets/app.scss'

import {createApp, onMounted, ref} from 'vue'
import router from '@/router/router.ts'
import App from '@/App.vue'

import z from 'zod'
import { nl } from 'zod/locales'
import { createPinia } from 'pinia'
import { useUserStore } from '@/stores/user.ts'
import { installAuthInterceptors } from '@/router/auth-intercept.ts'
import { createI18n } from 'vue-i18n'
import {
  collectModelMessages,
  createI18nTranslator,
  createModels,
  i18nLocale,
  mergeLocaleMessages,
} from '@jantm/model-core'
import appMessages from "./locales"
import {apiClient} from "@/api/http.ts";
import {createAddressModel} from "@/types/Wiser/address.ts";
import {createCertificateModel} from "@/types/Wiser/certificate.ts";
import {createContactPersonModel} from "@/types/Wiser/contactPerson.ts";
import {createInvoiceModel} from "@/types/Wiser/invoice.ts";
import {createOfferModel} from "@/types/Wiser/offer.ts";
import {createRelationModel} from "@/types/Wiser/relation.ts";
import {createScheduleModel} from "@/types/Wiser/schedule.ts";
import {createStudentModel} from "@/types/Wiser/student.ts";

z.config(nl())

const models = [
  createAddressModel({ client: apiClient }),
  createCertificateModel({ client: apiClient }),
  createContactPersonModel({ client: apiClient }),
  createInvoiceModel({ client: apiClient }),
  createOfferModel({ client: apiClient }),
  createRelationModel({ client: apiClient }),
  createScheduleModel({ client: apiClient }),
  createStudentModel({ client: apiClient })
]

const i18n = createI18n({
  legacy: false,
  locale: 'nl',
  fallbackLocale: 'en',
  // Model packages first, the application's own file last — so an override in
  // the application wins without anyone touching the package.
  messages: mergeLocaleMessages(collectModelMessages(models), appMessages),
})


const app = createApp(App)
const pinia = createPinia()

app.use(router)
app.use(pinia)
app.use(i18n)
app.use(
  createModels(models, {
    t: createI18nTranslator(i18n),
    // Numbers and dates too, not just text: without this `toLocaleString`
    // follows the browser, so an English page renders "84.210 km" on a Dutch
    // machine. It is a getter, so a language switch re-formats them.
    locale: i18nLocale(i18n),
  }),
)
app.provide('initialLocation', new URL(location.href, location.origin))

const userStore = useUserStore()
const removeAuthInterceptors = installAuthInterceptors(userStore)

await Promise.all([router.isReady(), userStore.restoreSession()])

app.mount('#app')

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    removeAuthInterceptors()
  })
}
